// System Terminology Configuration
// This configuration is managed by System Administrators and affects all users

import type { SystemTerminologyConfigurationApi } from "@/api/models/systemTerminologyConfiguration";
import type { TransactionCategory } from "@/types";
import { formatDate } from "./formatting";

export type CashDurationType =
  | "coverage"
  | "runway"
  | "reserve"
  | "buffer"
  | "date"
  | "custom";
export type CashMovementType =
  | "incoming-outgoing"
  | "inflows-outflows"
  | "receipts-payments"
  | "custom";
export type ForecastTermType =
  | "forecast"
  | "projection"
  | "outlook"
  | "plan"
  | "custom";
export type ScenarioTermType =
  | "scenarios"
  | "what-ifs"
  | "plans"
  | "models"
  | "custom";
export type VarianceTermType =
  | "variance"
  | "deviation"
  | "difference"
  | "delta"
  | "custom";
export type ActionsTermType =
  | "actions"
  | "tasks"
  | "to-dos"
  | "follow-ups"
  | "custom";

export interface SystemTerminologyConfig {
  cashDuration: CashDurationType;
  cashDurationCustom?: string;
  cashMovement: CashMovementType;
  cashMovementInCustom?: string;
  cashMovementOutCustom?: string;
  forecastTerm: ForecastTermType;
  forecastTermCustom?: string;
  scenarioTerm: ScenarioTermType;
  scenarioTermCustom?: string;
  varianceTerm: VarianceTermType;
  varianceTermCustom?: string;
  actionsTerm: ActionsTermType;
  actionsTermCustom?: string;
}

// Default configuration (recommended settings)
export const defaultTerminologyConfig: SystemTerminologyConfig = {
  cashDuration: "coverage",
  cashMovement: "incoming-outgoing",
  forecastTerm: "forecast",
  scenarioTerm: "scenarios",
  varianceTerm: "variance",
  actionsTerm: "actions",
};

// Storage key — uses sessionStorage so changes reset on hard refresh (demo safety)
// In live mode the backend owns this config — sessionStorage is the correct interim store
const STORAGE_KEY = "system-terminology-config";

// Clear any stale localStorage entry from previous builds
try {
  localStorage.removeItem(STORAGE_KEY);
} catch (_) {}

// Load configuration from sessionStorage or use default
function loadTerminology(): SystemTerminologyConfig {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Failed to load terminology config:", error);
  }
  return { ...defaultTerminologyConfig };
}

// In-memory cache (synced with sessionStorage)
let systemTerminology: SystemTerminologyConfig = loadTerminology();

export function getSystemTerminology(): SystemTerminologyConfig {
  // Always reload from sessionStorage to ensure we have the latest
  systemTerminology = loadTerminology();
  return { ...systemTerminology };
}

export function updateSystemTerminology(config: SystemTerminologyConfig): void {
  systemTerminology = { ...config };
  // Save to sessionStorage — resets on hard refresh, persists within the session
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    console.log("System terminology updated:", config);

    // Dispatch a custom event so other components can react to the change
    window.dispatchEvent(
      new CustomEvent("terminology-updated", { detail: config }),
    );
  } catch (error) {
    console.error("Failed to save terminology config:", error);
  }
}

// Pure helper that takes a config object - useful for live preview
export function getCashDurationTermFromConfig(
  config: SystemTerminologyConfig,
  months?: number,
  cashEndDate?: Date,
): string {
  if (months !== undefined && !isFinite(months)) {
    return "No burn";
  }
  switch (config.cashDuration) {
    case "coverage":
      return months !== undefined ? `${months} months coverage` : "coverage";
    case "runway":
      return months !== undefined ? `${months} months runway` : "runway";
    case "reserve":
      return months !== undefined ? `${months} months reserve` : "reserve";
    case "buffer":
      return months !== undefined
        ? `${months} months operating buffer`
        : "operating buffer";
    case "date":
      if (cashEndDate) {
        return `until ${formatDate(cashEndDate, undefined, { day: "numeric", month: "short", year: "numeric" })}`;
      }
      return "until date";
    case "custom":
      return config.cashDurationCustom || "coverage";
    default:
      return months !== undefined ? `${months} months coverage` : "coverage";
  }
}

export function getCashDurationTerm(
  months?: number,
  cashEndDate?: Date,
): string {
  const config = getSystemTerminology(); // Always get fresh config from sessionStorage
  return getCashDurationTermFromConfig(config, months, cashEndDate);
}

// Pure helper that takes a config object - useful for live preview
export function getCashMovementTermsFromConfig(
  config: SystemTerminologyConfig,
): { in: string; out: string } {
  switch (config.cashMovement) {
    case "incoming-outgoing":
      return { in: "Incoming", out: "Outgoing" };
    case "inflows-outflows":
      return { in: "Inflows", out: "Outflows" };
    case "receipts-payments":
      return { in: "Receipts", out: "Payments" };
    case "custom":
      return {
        in: config.cashMovementInCustom || "Incoming",
        out: config.cashMovementOutCustom || "Outgoing",
      };
    default:
      return { in: "Incoming", out: "Outgoing" };
  }
}

export function getCashMovementTerms(): { in: string; out: string } {
  const config = getSystemTerminology(); // Always get fresh config from sessionStorage
  return getCashMovementTermsFromConfig(config);
}

// Pure helper that takes a config object - useful for live preview
export function getForecastTermFromConfig(
  config: SystemTerminologyConfig,
  capitalize: boolean = true,
): string {
  let term: string;
  switch (config.forecastTerm) {
    case "forecast":
      term = "forecast";
      break;
    case "projection":
      term = "projection";
      break;
    case "outlook":
      term = "outlook";
      break;
    case "plan":
      term = "plan";
      break;
    case "custom":
      term = config.forecastTermCustom || "forecast";
      break;
    default:
      term = "forecast";
  }

  return capitalize ? term.charAt(0).toUpperCase() + term.slice(1) : term;
}

export function getForecastTerm(capitalize: boolean = true): string {
  const config = getSystemTerminology(); // Always get fresh config from sessionStorage
  return getForecastTermFromConfig(config, capitalize);
}

// Helper function to get short cash duration term (without months)
export function getCashDurationShortTerm(): string {
  const config = getSystemTerminology(); // Always get fresh config from sessionStorage

  switch (config.cashDuration) {
    case "coverage":
      return "coverage";
    case "runway":
      return "runway";
    case "reserve":
      return "reserve";
    case "buffer":
      return "operating buffer";
    case "date":
      return "cash until";
    case "custom":
      return config.cashDurationCustom || "coverage";
    default:
      return "coverage";
  }
}

// Naive singularisation for custom terms — drops a trailing plural "s".
function singularize(word: string): string {
  return word.endsWith("s") ? word.slice(0, -1) : word;
}

// ─── Scenario Term ────────────────────────────────────────────────────────────

const SCENARIO_FORMS: Record<
  Exclude<ScenarioTermType, "custom">,
  { one: string; many: string }
> = {
  scenarios: { one: "scenario", many: "scenarios" },
  "what-ifs": { one: "what-if", many: "what-ifs" },
  plans: { one: "plan", many: "plans" },
  models: { one: "model", many: "models" },
};

export function getScenarioTermFromConfig(
  config: SystemTerminologyConfig,
  capitalize = true,
  plural = true,
): string {
  let term: string;
  if (config.scenarioTerm === "custom") {
    const custom = config.scenarioTermCustom || "scenarios";
    term = plural ? custom : singularize(custom);
  } else {
    const forms =
      SCENARIO_FORMS[config.scenarioTerm] ?? SCENARIO_FORMS.scenarios;
    term = plural ? forms.many : forms.one;
  }
  return capitalize ? term.charAt(0).toUpperCase() + term.slice(1) : term;
}

export function getScenarioTerm(capitalize = true, plural = true): string {
  return getScenarioTermFromConfig(getSystemTerminology(), capitalize, plural);
}

// ─── Variance Term ────────────────────────────────────────────────────────────

export function getVarianceTermFromConfig(
  config: SystemTerminologyConfig,
  capitalize = true,
): string {
  let term: string;
  switch (config.varianceTerm) {
    case "variance":
      term = "variance";
      break;
    case "deviation":
      term = "deviation";
      break;
    case "difference":
      term = "difference";
      break;
    case "delta":
      term = "delta";
      break;
    case "custom":
      term = config.varianceTermCustom || "variance";
      break;
    default:
      term = "variance";
  }
  return capitalize ? term.charAt(0).toUpperCase() + term.slice(1) : term;
}

export function getVarianceTerm(capitalize = true): string {
  return getVarianceTermFromConfig(getSystemTerminology(), capitalize);
}

// ─── Actions Term ─────────────────────────────────────────────────────────────

const ACTIONS_FORMS: Record<
  Exclude<ActionsTermType, "custom">,
  { one: string; many: string }
> = {
  actions: { one: "action", many: "actions" },
  tasks: { one: "task", many: "tasks" },
  "to-dos": { one: "to-do", many: "to-dos" },
  "follow-ups": { one: "follow-up", many: "follow-ups" },
};

export function getActionsTermFromConfig(
  config: SystemTerminologyConfig,
  capitalize = true,
  plural = true,
): string {
  let term: string;
  if (config.actionsTerm === "custom") {
    const custom = config.actionsTermCustom || "actions";
    term = plural ? custom : singularize(custom);
  } else {
    const forms = ACTIONS_FORMS[config.actionsTerm] ?? ACTIONS_FORMS.actions;
    term = plural ? forms.many : forms.one;
  }
  return capitalize ? term.charAt(0).toUpperCase() + term.slice(1) : term;
}

export function getActionsTerm(capitalize = true, plural = true): string {
  return getActionsTermFromConfig(getSystemTerminology(), capitalize, plural);
}

// ─── Label Substitution ───────────────────────────────────────────────────────

// Naive pluralisation for custom terms — adds a trailing "s" when missing.
function pluralize(word: string): string {
  return word.endsWith("s") ? word : `${word}s`;
}

// Matches the default terms in a hardcoded English display string.
const DEFAULT_TERM_PATTERN =
  /\b(scenarios|scenario|variances|variance|actions|action|forecasts|forecast)\b/gi;

// Mirrors the casing of the matched word onto the configured term.
function matchCase(match: string, term: string): string {
  if (match === match.toUpperCase()) return term.toUpperCase();
  if (match[0] === match[0].toUpperCase()) {
    return term.charAt(0).toUpperCase() + term.slice(1);
  }
  return term.toLowerCase();
}

// Replaces the default terms in a display string with the configured ones,
// preserving casing and singular/plural form.
export function applyTerminologyToLabelFromConfig(
  config: SystemTerminologyConfig,
  label: string,
): string {
  return label.replace(DEFAULT_TERM_PATTERN, (match) => {
    const lower = match.toLowerCase();
    const plural = lower.endsWith("s");

    let term: string;
    if (lower.startsWith("scenario")) {
      term = getScenarioTermFromConfig(config, false, plural);
    } else if (lower.startsWith("variance")) {
      const variance = getVarianceTermFromConfig(config, false);
      term = plural ? pluralize(variance) : variance;
    } else if (lower.startsWith("action")) {
      term = getActionsTermFromConfig(config, false, plural);
    } else {
      const forecast = getForecastTermFromConfig(config, false);
      term = plural ? pluralize(forecast) : forecast;
    }

    return matchCase(match, term);
  });
}

export function applyTerminologyToLabel(label: string): string {
  return applyTerminologyToLabelFromConfig(getSystemTerminology(), label);
}

// Helper function to get category labels that respect terminology settings
export function getCategoryLabel(category: TransactionCategory): string {
  const terms = getCashMovementTerms();

  const labels: Record<TransactionCategory, string> = {
    Revenue: terms.in,
    "Other Income": `Other ${terms.in}`,
    Materials: "Materials",
    Payroll: "Payroll",
    Subcontractors: "Subcontractors",
    Equipment: "Equipment",
    "Equipment Rental": "Equipment Rental",
    Utilities: "Utilities",
    Rent: "Rent",
    Insurance: "Insurance",

    "Professional Services": "Professional Services",
    "Office Expenses": "Office Expenses",
    "Vehicle Expenses": "Vehicle Expenses",
    Travel: "Travel",
    Marketing: "Marketing",
    Training: "Training",
    "Bank Fees": "Bank Fees",
    Tax: "Tax",
    Uncategorised: "Uncategorised",
  };

  return labels[category];
}

// Map API response to UI model — shared by bootstrap and admin settings page
export function mapApiToUiModel(
  api: SystemTerminologyConfigurationApi,
): SystemTerminologyConfig {
  const terminologies = api.terminologies;
  return {
    cashDuration: (terminologies["cash-duration"] ??
      "coverage") as SystemTerminologyConfig["cashDuration"],
    cashDurationCustom: terminologies["cash-duration-custom"] ?? undefined,
    cashMovement: (terminologies["cash-movement"] ??
      "incoming-outgoing") as SystemTerminologyConfig["cashMovement"],
    cashMovementInCustom: terminologies["cash-movement-custom-in"] ?? undefined,
    cashMovementOutCustom:
      terminologies["cash-movement-custom-out"] ?? undefined,
    forecastTerm: (terminologies["forecast-term"] ??
      "forecast") as SystemTerminologyConfig["forecastTerm"],
    forecastTermCustom: terminologies["forecast-term-custom"] ?? undefined,
    scenarioTerm: (terminologies["scenario-term"] ??
      "scenarios") as SystemTerminologyConfig["scenarioTerm"],
    scenarioTermCustom: terminologies["scenario-term-custom"] ?? undefined,
    varianceTerm: (terminologies["variance-term"] ??
      "variance") as SystemTerminologyConfig["varianceTerm"],
    varianceTermCustom: terminologies["variance-term-custom"] ?? undefined,
    actionsTerm: (terminologies["actions-term"] ??
      "actions") as SystemTerminologyConfig["actionsTerm"],
    actionsTermCustom: terminologies["actions-term-custom"] ?? undefined,
  };
}
